import { Home } from '@mui/icons-material'
import { Breadcrumbs, Link, Tooltip } from '@mui/material'
import { t } from 'i18next'
import { hyphenToCamelCase } from '../../store/utils'
import navigation from '../../config/navigation'
import { useAppSelector } from '../../store/redux/hooks'
import { BreadcrumbsItemsState, getBreadcrumbsItemsState } from '../../store/redux/reducers/breadcrumbsSlice'
import { Link as RouterLink } from 'react-router-dom'
import React from 'react'
import { environment } from '../../enviroments/enviroment'

interface BreadcrumbItem {
  label: string | null | undefined
  link?: string
}

const pathsWithLinks = [
  'users',
  'projects',
  'demands',
  'candidates',
  'edit-recruitment',
  'yearly-summary',
  'schedule',
  'worklog',
  'admin-settings',
  'vacations',
  'reports',
  'projects-distribution',
  'settlements',
  'cost-summary',
  'my-profile',
  'leader-view',
]

const BreadcrumbsContainer = () => {
  const pathname = window.location.pathname
  const breadcrumbsItemsState = useAppSelector(getBreadcrumbsItemsState)

  const generateBreadcrumbs = (pathname: string) => {
    const pathSegments = pathname.split('/').filter(segment => segment !== '')
    const basename = environment.publicUrl
    const breadcrumbItems: BreadcrumbItem[] = [{ label: t('page.name.home'), link: '/home' }]
    let currentPath = ''
    pathSegments.forEach((segment, index, array) => {
      currentPath += `/${segment}`
      if (currentPath === basename || currentPath === `${basename}/`) return

      if (currentPath.startsWith(basename)) {
        currentPath = currentPath.substring(basename.length)
      }

      const isLastSegment = array.length - 1 === index
      const isNonHomeLast = isLastSegment && currentPath !== navigation.home
      const isSpecialPath = [
        navigation.home,
        navigation.worklog,
        navigation.yearlySummary,
        navigation.schedule,
      ].includes(currentPath)

      if (!isNonHomeLast && isSpecialPath) return
      if (pathSegments[index - 1] && pathsWithLinks.includes(pathSegments[index - 1])) {
        if (pathSegments[index - 1] === 'yearly-summary') {
          breadcrumbItems.push({
            label: breadcrumbsItemsState['yearlySummary' as keyof BreadcrumbsItemsState],
            link: `/${pathSegments[index - 1]}/${pathSegments[index]}`,
          })
          return
        }
        if (pathSegments[index - 1] === 'admin-settings') {
          breadcrumbItems.push({
            label: breadcrumbsItemsState['adminSettings' as keyof BreadcrumbsItemsState],
            link: `/${pathSegments[index - 1]}/${pathSegments[index]}`,
          })
          return
        }
        if (pathSegments[index - 1] === 'edit-recruitment') {
          return
        }

        const labelKey = pathSegments[index - 1] as keyof BreadcrumbsItemsState
        const label = breadcrumbsItemsState[labelKey]
        if (!label) return

        breadcrumbItems.push({ label, link: currentPath })
        return
      } else if (pathSegments[index] && pathsWithLinks.includes(pathSegments[index])) {
        if (pathSegments[index] === 'yearly-summary') {
          breadcrumbItems.push({
            label: breadcrumbsItemsState['yearlySummary' as keyof BreadcrumbsItemsState],
            link: currentPath,
          })
          return
        }

        if (pathSegments[index] === 'edit-recruitment') {
          breadcrumbItems.push({
            label: t(`page.name.${hyphenToCamelCase(segment)}`),
            link: `${currentPath}/${pathSegments[index + 1]}`,
          })
          return
        }

        const validSegments = ['projects-distribution', 'cost-summary', 'my-profile', 'add-demand', 'leader-view']

        if (validSegments.includes(pathSegments[index])) {
          breadcrumbItems.push({
            label: t(`page.name.${hyphenToCamelCase(segment)}`),
            link: `${currentPath}`,
          })
          return
        }

        breadcrumbItems.push({
          label: t(`page.name.${hyphenToCamelCase(segment)}`),
          link: `/${pathSegments[index]}`,
        })
        return
      } else if (pathSegments[index - 1] && isLastSegment) {
        const isContractPath = pathSegments[index - 1] === 'contracts' || pathSegments[index - 2] === 'contracts'
        breadcrumbItems.push({
          label: t(`page.name.${hyphenToCamelCase(segment)}`),
          link: isContractPath ? currentPath : `/${pathSegments[index]}`,
        })
        return
      }
      breadcrumbItems.push({
        label: breadcrumbsItemsState[pathSegments[index - 1] as keyof BreadcrumbsItemsState],
        link: currentPath,
      })
    })
    return breadcrumbItems
  }

  const breadcrumbItems = generateBreadcrumbs(pathname).filter(item => Boolean(item.label))

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 11 }} maxItems={2}>
      {breadcrumbItems.length !== 1 &&
        breadcrumbItems.map((item, i) => (
          <Link
            aria-label={`${item.label}`}
            component={RouterLink}
            to={item.link || '#'}
            key={`${item.link}-breadcrumb-link`}
            underline="hover"
            color="inherit"
            sx={{
              color: i === breadcrumbItems.length - 1 ? 'text.highEmphasis' : 'text.base',
              display: 'flex',
              alignItems: 'center',
              mt: 0.5,
              maxWidth: { xs: '150px', sm: '250px', md: '300px' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label === t('page.name.home') ? (
              <Tooltip title={item.label}>
                <Home sx={{ width: 16, height: 16 }} aria-label="Dashboard" />
              </Tooltip>
            ) : (
              <Tooltip title={item.label}>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </span>
              </Tooltip>
            )}
          </Link>
        ))}
    </Breadcrumbs>
  )
}

export default BreadcrumbsContainer
